/**
 * DDR spend admin HTTP client - the `/api/ddr/usage/*` routes (backend
 * ddr_router.py spend section) and the `/wallet/extra-usage` routes (backend
 * wallet_extra_usage.py). Admin/owner only, except `/wallet/extra-usage/me`
 * and `/me/proactive`, which any member may call. The backend enforces the role.
 *
 * Two concerns:
 *   - CONFIG: usage groups (tiers) + per-user assignment + workspace ceiling.
 *   - REPORTING: spend summary, per-user, by-origin ("how it was spent"),
 *     origin drill (which jobs/triggers), daily time-series.
 *
 * Style mirrors DdrService: HttpClient + firstValueFrom, interceptor handles
 * auth, raw passthrough (caller decides on validation).
 */

import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';

const ROOT = `${environment.apiBase}/api/ddr/usage`;
const EXTRA_USAGE = `${environment.apiBase}/wallet/extra-usage`;

/** "Allow extra usage" past a plan cap (CIQ-2230). Only an owner may change the all-members switch or an owner's setting. */
export type ExtraUsageSettings = {
    all_members: boolean;
    user_ids: number[];
    owner_user_ids: number[];
    viewer_is_owner: boolean;
    viewer_user_id?: number;
    viewer_email?: string;
    /** Phase 2: when each member's grant ends, keyed by user id. */
    grant_expires_at?: Record<string, string>;
};

/** The caller's own extra usage, and whether proactive DDR tasks may use it (on unless it was turned off). */
export type OwnExtraUsage = {
    extra_usage: boolean;
    proactive: boolean;
    /** Sent only when a cap window is asked for: a live plan that is not a trial, where extra usage applies. */
    available?: boolean;
    /** Sent only when a cap window is asked for: an owner or admin on such a plan, who turns it on in Usage & Limits. */
    can_turn_on?: boolean;
    /** Sent only when a cap window is asked for: the caller's request in that window, if any. */
    request_status?: ExtraUsageRequestStatus | null;
};

export type ExtraUsageWindow = 'daily' | 'monthly';
export type ExtraUsageRequestStatus = 'pending' | 'approved' | 'dismissed';

export type ExtraUsageRequest = {
    id: number;
    user_id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    meter: string;
    window: ExtraUsageWindow;
    created_at: string | null;
};

export type ExtraUsageRequestPage = { requests: ExtraUsageRequest[]; total: number };

function viewerTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Personal reads omit `tz`, so the server uses the member's stored timezone, as the Yesterday card does. */
function windowParams(days: SpendWindow, sendTimeZone = true): Record<string, string> {
    if (typeof days === 'number') {
        return { days: String(days) };
    }
    return days === 'yesterday' && sendTimeZone ? { window: days, tz: viewerTimeZone() } : { window: days };
}

function windowQuery(days: SpendWindow, sendTimeZone = true): string {
    return new HttpParams({ fromObject: windowParams(days, sendTimeZone) }).toString();
}

export type UsageGroup = {
    id: number;
    name: string;
    is_default: boolean;
    daily_limit_usd: number | null;
    monthly_limit_usd: number | null;
    proactive_reserve_pct: number;
    /** Phase 2 only: the group a member moves to while extra usage is on. */
    extra_usage_group_id?: number | null;
    extra_usage_window_hours?: number | null;
};

export type UsageGroupInput = {
    name: string;
    daily_limit_usd: number | null;
    monthly_limit_usd: number | null;
    proactive_reserve_pct: number;
    is_default: boolean;
};

export type WorkspaceSpendConfig = {
    monthly_ceiling_usd: number | null;
    enabled: boolean;
    show_cost_to_users?: boolean;
    /** Always null since limits became opt-in; kept for response-shape compat. */
    default_ceiling_usd: number | null;
};

export type NamedSpendWindow = 'mtd' | '24h' | 'yesterday';

export type SpendWindow = number | NamedSpendWindow;

export type SpendWindowOption = { value: SpendWindow; label: string };

export const SPEND_WINDOW_OPTIONS: readonly SpendWindowOption[] = [
    { value: 'mtd', label: 'Month to date' },
    { value: 2, label: 'Last 2 days' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 182, label: 'Last 6 months' },
    { value: 365, label: 'Last year' },
];

const PARTIAL_DAY_WINDOW_OPTIONS: readonly SpendWindowOption[] = [
    { value: '24h', label: 'Last 24 hours' },
    { value: 'yesterday', label: 'Yesterday' },
];

/** Corporate workspaces phase 2 adds Last 24 hours and Yesterday after Month to date. */
export function spendWindowOptions(phase2: boolean): readonly SpendWindowOption[] {
    return phase2 ? [SPEND_WINDOW_OPTIONS[0], ...PARTIAL_DAY_WINDOW_OPTIONS, ...SPEND_WINDOW_OPTIONS.slice(1)] : SPEND_WINDOW_OPTIONS;
}

export type OriginSpend = { origin: string; calls: number; total_usd: number };
export type OriginRefSpend = { origin_ref: string; calls: number; total_usd: number };

export type SpendSummary = {
    window_days: number;
    window?: NamedSpendWindow | null;
    window_total_usd: number;
    by_origin: OriginSpend[];
    month_to_date_usd: number;
    monthly_ceiling_usd: number | null;
};

export type UserSpend = {
    user_id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    group_id: number | null;
    calls: number;
    total_usd: number;
    /** Main chat prompts sent in the window; null unless the page asked for prompt counts. */
    prompts: number | null;
    daily_spent_usd: number;
    daily_limit_usd: number | null;
    monthly_spent_usd: number;
    monthly_limit_usd: number | null;
    daily_limit_reached: boolean;
    monthly_limit_reached: boolean;
    /** Present only when the page asked for organization units. */
    unit_id?: number | null;
    /** Connector keys switched off for this member; absent while member connector controls are off. */
    connector_blocks?: MemberConnectorKey[];
};

export type MemberConnectorKey = 'back_office' | 'inside_track' | 'arrowstream';

export type MemberConnector = { key: MemberConnectorKey; name: string };

/** 'all', 'unassigned' (no explicit usage-group assignment), or a usage-group id. */
export type GroupFilter = 'all' | 'unassigned' | string;

/** `rank_available` is present when the query sets `includeRankStatus` and for every sort other than name. */
export type UserSpendPage = { items: UserSpend[]; total: number; rank_available?: boolean; member_connectors?: MemberConnector[] };

export type SpendSort = 'name' | 'main_chat' | 'ddr' | 'app' | 'month' | 'prompts';

export const SPEND_SORT_OPTIONS: readonly { value: SpendSort; label: string }[] = [
    { value: 'name', label: 'Name (A–Z)' },
    { value: 'main_chat', label: 'Chat usage' },
    { value: 'ddr', label: 'DDR usage' },
    { value: 'app', label: 'App building' },
    { value: 'month', label: 'Spend' },
    { value: 'prompts', label: 'Prompts' },
];

export type UserSpendPageQuery = {
    days: SpendWindow;
    page: number;
    pageSize: number;
    q?: string;
    groupFilter?: GroupFilter;
    sort?: SpendSort;
    includePrompts?: boolean;
    includeRankStatus?: boolean;
    /** 'all', 'unassigned' or a unit id. Undefined leaves units out of the page. */
    unitFilter?: string;
};

export type BulkSelection =
    { user_ids: number[] } | { filter: { q: string; group_filter: string; unit_filter: string }; exclude_user_ids: number[] };

export type BulkMemberAction =
    | { kind: 'set_group'; group_id: number }
    | { kind: 'set_unit'; unit_id: number | null }
    | { kind: 'extra_usage'; allowed: boolean; window_minutes?: number | null }
    | { kind: 'connectors'; connector_key: MemberConnectorKey; allowed: boolean };

export type BulkResult = { updated: number; skipped: { user_id: number; reason: string }[] };

export type TimeseriesPoint = { day: string; total_usd: number };

export type UserSpendDetail = {
    user_id: number;
    by_origin: OriginSpend[];
    timeseries: TimeseriesPoint[];
};

export type MyUsageStatus = {
    has_limits: boolean;
    /** Admin switch (corporate wallets): false = dollar fields are null, use pct. */
    show_cost?: boolean;
    today_usd?: number | null;
    daily_limit_usd?: number | null;
    month_usd?: number | null;
    monthly_limit_usd?: number | null;
    reserve_usd?: number | null;
    today_pct?: number | null;
    month_pct?: number | null;
    last_24h_usd?: number | null;
    yesterday_usd?: number | null;
    last_24h_pct?: number | null;
    yesterday_pct?: number | null;
    proactive_paused: boolean;
    chat_paused?: boolean;
    chat_block_kind?: string | null;
    proactive_full_budget: boolean;
};

export type PersonalLimits = {
    daily_limit_usd: number | null;
    monthly_limit_usd: number | null;
    today_usd: number;
    month_to_date_usd: number;
    has_wallet: boolean;
    has_explicit_limits: boolean;
};

export type PersonalSpendSummary = {
    window_days: number;
    window?: NamedSpendWindow | null;
    show_cost?: boolean;
    window_total_usd: number | null;
    // total_usd is re-declared nullable: the backend nulls it out when the
    // workspace hides costs from members (show_cost=false). A plain
    // intersection with OriginSpend would collapse `number | null & number`
    // back to `number`, so Omit first.
    by_origin: (Omit<OriginSpend, 'total_usd'> & { pct_of_window?: number; total_usd: number | null })[];
    month_to_date_usd: number | null;
    monthly_ceiling_usd: number | null;
    month_pct?: number | null;
};

export type PersonalTimeseriesPoint = { day: string; total_usd: number | null; pct_of_limit?: number };

@Injectable({ providedIn: 'root' })
export class DdrSpendService {
    private readonly http = inject(HttpClient);

    // ── Config ──
    listGroups(): Promise<UsageGroup[]> {
        return firstValueFrom(this.http.get<UsageGroup[]>(`${ROOT}/groups`));
    }

    createGroup(body: UsageGroupInput): Promise<{ id: number }> {
        return firstValueFrom(this.http.post<{ id: number }>(`${ROOT}/groups`, body));
    }

    updateGroup(id: number, body: UsageGroupInput): Promise<{ ok: boolean }> {
        return firstValueFrom(this.http.put<{ ok: boolean }>(`${ROOT}/groups/${id}`, body));
    }

    deleteGroup(id: number): Promise<{ ok: boolean }> {
        return firstValueFrom(this.http.delete<{ ok: boolean }>(`${ROOT}/groups/${id}`));
    }

    bulkMembers(selection: BulkSelection, action: BulkMemberAction): Promise<BulkResult> {
        return firstValueFrom(this.http.post<BulkResult>(`${ROOT}/members/bulk`, { selection, action }));
    }

    assignUserGroup(userId: number, groupId: number): Promise<{ ok: boolean }> {
        return firstValueFrom(this.http.put<{ ok: boolean }>(`${ROOT}/users/${userId}/group`, { group_id: groupId }));
    }

    getConfig(): Promise<WorkspaceSpendConfig> {
        return firstValueFrom(this.http.get<WorkspaceSpendConfig>(`${ROOT}/config`));
    }

    setConfig(monthlyCeilingUsd: number | null, enabled: boolean, showCostToUsers = false): Promise<{ ok: boolean }> {
        return firstValueFrom(
            this.http.put<{ ok: boolean }>(`${ROOT}/config`, {
                monthly_ceiling_usd: monthlyCeilingUsd,
                enabled,
                show_cost_to_users: showCostToUsers,
            })
        );
    }

    // ── Extra usage past a plan cap. The routes answer 404 while the feature is off. ──
    getExtraUsage(): Promise<ExtraUsageSettings> {
        return firstValueFrom(this.http.get<ExtraUsageSettings>(EXTRA_USAGE));
    }

    setExtraUsageAllMembers(allMembers: boolean): Promise<ExtraUsageSettings> {
        return firstValueFrom(this.http.put<ExtraUsageSettings>(EXTRA_USAGE, { all_members: allMembers }));
    }

    setMemberExtraUsage(userId: number, allowed: boolean, windowMinutes?: number): Promise<ExtraUsageSettings> {
        const body = windowMinutes ? { allowed, window_minutes: windowMinutes } : { allowed };
        return firstValueFrom(this.http.put<ExtraUsageSettings>(`${EXTRA_USAGE}/members/${userId}`, body));
    }

    setGroupExtraUsage(groupId: number, extraUsageGroupId: number | null, windowHours: number | null): Promise<{ ok: boolean }> {
        return firstValueFrom(
            this.http.put<{ ok: boolean }>(`${ROOT}/groups/${groupId}/extra-usage`, {
                extra_usage_group_id: extraUsageGroupId,
                extra_usage_window_hours: windowHours,
            })
        );
    }

    getOwnExtraUsage(window?: ExtraUsageWindow): Promise<OwnExtraUsage> {
        const params: Record<string, string> = window ? { window } : {};
        return firstValueFrom(this.http.get<OwnExtraUsage>(`${EXTRA_USAGE}/me`, { params }));
    }

    requestExtraUsage(meter: string, window: ExtraUsageWindow): Promise<{ request_status: ExtraUsageRequestStatus }> {
        return firstValueFrom(this.http.post<{ request_status: ExtraUsageRequestStatus }>(`${EXTRA_USAGE}/requests`, { meter, window }));
    }

    listExtraUsageRequests(query: { page: number; pageSize: number; q: string }): Promise<ExtraUsageRequestPage> {
        const params: Record<string, string> = {
            limit: String(query.pageSize),
            offset: String((query.page - 1) * query.pageSize),
        };
        if (query.q.trim()) {
            params['q'] = query.q.trim();
        }
        return firstValueFrom(this.http.get<ExtraUsageRequestPage>(`${EXTRA_USAGE}/requests`, { params }));
    }

    resolveExtraUsageRequest(id: number, action: 'approve' | 'dismiss'): Promise<{ settings: ExtraUsageSettings }> {
        return firstValueFrom(this.http.post<{ settings: ExtraUsageSettings }>(`${EXTRA_USAGE}/requests/${id}/${action}`, {}));
    }

    setOwnProactiveExtraUsage(enabled: boolean): Promise<OwnExtraUsage> {
        return firstValueFrom(this.http.put<OwnExtraUsage>(`${EXTRA_USAGE}/me/proactive`, { enabled }));
    }

    // ── Reporting ──
    summary(days: SpendWindow): Promise<SpendSummary> {
        return firstValueFrom(this.http.get<SpendSummary>(`${ROOT}/spend/summary?${windowQuery(days)}`));
    }

    /** Roster-driven per-user spend page — workspace members (zero spend
     *  included), searchable + filterable by usage group, server-paginated
     *  name A→Z or by `sort`. Spend is computed only for the visible page (ADR-0029). */
    users(query: UserSpendPageQuery): Promise<UserSpendPage> {
        let params = new HttpParams({ fromObject: windowParams(query.days) })
            .set('page', String(query.page))
            .set('page_size', String(query.pageSize));
        const q = query.q?.trim();
        if (q) {
            params = params.set('q', q);
        }
        if (query.groupFilter && query.groupFilter !== 'all') {
            params = params.set('group_filter', query.groupFilter);
        }
        if (query.sort && query.sort !== 'name') {
            params = params.set('sort', query.sort);
        }
        if (query.includePrompts) {
            params = params.set('include_prompts', 'true');
        }
        if (query.includeRankStatus) {
            params = params.set('include_rank_status', 'true');
        }
        if (query.unitFilter !== undefined) {
            params = params.set('unit_filter', query.unitFilter);
        }
        return firstValueFrom(this.http.get<UserSpendPage>(`${ROOT}/spend/users`, { params }));
    }

    userDetail(userId: number, days: SpendWindow): Promise<UserSpendDetail> {
        return firstValueFrom(this.http.get<UserSpendDetail>(`${ROOT}/spend/users/${userId}?${windowQuery(days)}`));
    }

    originDrill(origin: string, days: SpendWindow): Promise<OriginRefSpend[]> {
        return firstValueFrom(this.http.get<OriginRefSpend[]>(`${ROOT}/spend/origin/${origin}?${windowQuery(days)}`));
    }

    timeseries(days: SpendWindow): Promise<TimeseriesPoint[]> {
        return firstValueFrom(this.http.get<TimeseriesPoint[]>(`${ROOT}/spend/timeseries?${windowQuery(days)}`));
    }

    // ── Self-service (any user) ──
    myStatus(): Promise<MyUsageStatus> {
        return firstValueFrom(this.http.get<MyUsageStatus>(`${ROOT}/me`));
    }

    setProactiveFullBudget(enabled: boolean): Promise<{ ok: boolean }> {
        return firstValueFrom(this.http.put<{ ok: boolean }>(`${ROOT}/me/proactive_budget`, { enabled }));
    }

    getMyLimits(): Promise<PersonalLimits> {
        return firstValueFrom(this.http.get<PersonalLimits>(`${ROOT}/me/limits`));
    }

    setMyLimits(
        daily: number | null,
        monthly: number | null
    ): Promise<{ ok: boolean; daily_limit_usd: number | null; monthly_limit_usd: number | null }> {
        return firstValueFrom(
            this.http.put<{ ok: boolean; daily_limit_usd: number | null; monthly_limit_usd: number | null }>(`${ROOT}/me/limits`, {
                daily_limit_usd: daily,
                monthly_limit_usd: monthly,
            })
        );
    }

    myUsageSummary(days: SpendWindow): Promise<PersonalSpendSummary> {
        return firstValueFrom(this.http.get<PersonalSpendSummary>(`${ROOT}/me/summary?${windowQuery(days, false)}`));
    }

    myTimeseries(days: SpendWindow): Promise<PersonalTimeseriesPoint[]> {
        return firstValueFrom(this.http.get<PersonalTimeseriesPoint[]>(`${ROOT}/me/timeseries?${windowQuery(days, false)}`));
    }
}
